import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import "../../../i18n";
import { Expander, KeyValues, MethodChip, Notice, StatusPill } from "../../../design/components";
import { dateTime } from "../../../lib/format";
import { saveBlob } from "../../../lib/saveBlob";
import type { Entity } from "../../../api/types";
import {
  useFileBatches,
  useFileTemplate,
  useFileTemplateDescriptor,
  type FileBatch,
} from "../../../api/fileIntake";
import styles from "./FileIntakePanel.module.css";

const EMPTY_BATCHES = "No batches received yet. Drop a completed template into the inbox folder.";
const DEFAULT_PICKUP_SECONDS = 300;

/** `config` is the raw ERP-side jsonb; `pickup_interval_seconds` stays snake_case (not a named API field). */
function pickupIntervalLabel(config: Record<string, unknown>, t: TFunction): string {
  const raw = config.pickup_interval_seconds;
  const parsed = typeof raw === "number" ? raw : Number(raw);
  const seconds = Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_PICKUP_SECONDS;
  if (seconds % 60 === 0) {
    const minutes = seconds / 60;
    return t("companies.fileIntake.pickupEveryMinutes", `every ${minutes} minute${minutes === 1 ? "" : "s"}`, {
      count: minutes,
    });
  }
  return t("companies.fileIntake.pickupEverySeconds", `every ${seconds} second${seconds === 1 ? "" : "s"}`, {
    count: seconds,
  });
}

function DownloadTemplateButton({
  entityId,
  entityCode,
  templateVersion,
}: {
  entityId: string;
  entityCode: string;
  templateVersion: string;
}) {
  const { t } = useTranslation();
  const template = useFileTemplate(entityId);

  async function handleClick(): Promise<void> {
    const blob = await template.mutateAsync();
    saveBlob(blob, `hub-file-template-v${templateVersion}-${entityCode}.zip`);
  }

  return (
    <button
      type="button"
      className="btn btn-sm btn-primary"
      disabled={template.isPending}
      onClick={() => void handleClick()}
    >
      {t("companies.fileIntake.downloadTemplate", "Download template")}
    </button>
  );
}

function BatchRow({ batch }: { batch: FileBatch }) {
  const { t } = useTranslation();
  return (
    <tr>
      <td>{batch.filename}</td>
      <td className="nowrap">{dateTime(batch.updatedAt)}</td>
      <td className="nowrap">{batch.rows}</td>
      <td>
        <StatusPill status={batch.state} />
      </td>
      <td>
        {batch.state === "rejected" && batch.detail ? (
          <Expander
            head={t("companies.fileIntake.rejectionDetail", "Rejection detail")}
            body={<p className={styles.detail}>{batch.detail}</p>}
          />
        ) : null}
      </td>
    </tr>
  );
}

/** Method 3's Connection-tab panel: drop location, template, last-50 batch status. */
export function FileIntakePanel({
  entity,
  config,
}: {
  entity: Entity;
  config: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  const isFile = entity.connectionMethod === "file";
  const descriptor = useFileTemplateDescriptor(entity.id, isFile);
  const batches = useFileBatches(entity.id, isFile);

  if (!isFile) return null;

  const rows = batches.data ?? [];

  return (
    <section className="card">
      <div className="card-head">
        <MethodChip method={3} full />
      </div>

      {descriptor.isError ? (
        <div className="card-body">
          <Notice kind="fail">
            {t("companies.fileIntake.templateLoadError", "The file template could not be loaded.")}
          </Notice>
        </div>
      ) : null}

      {descriptor.data ? (
        <div className="card-body">
          <KeyValues
            pairs={[
              [t("companies.fileIntake.inboxFolder", "Inbox folder"), descriptor.data.dropPath],
              [t("companies.fileIntake.resultsFolder", "Results folder"), descriptor.data.resultsPath],
              [t("companies.fileIntake.templateVersion", "Template version"), descriptor.data.templateVersion],
              [t("companies.fileIntake.pickupInterval", "Pickup interval"), pickupIntervalLabel(config, t)],
            ]}
          />
        </div>
      ) : null}

      <div className="card-head">
        <h2 className="grow">{t("companies.fileIntake.fileTemplateHeading", "File template")}</h2>
        {descriptor.data ? (
          <DownloadTemplateButton
            entityId={entity.id}
            entityCode={entity.code}
            templateVersion={descriptor.data.templateVersion}
          />
        ) : (
          <button type="button" className="btn btn-sm" disabled>
            {t("companies.fileIntake.downloadTemplate", "Download template")}
          </button>
        )}
      </div>
      {descriptor.data ? (
        <div className="card-body table-wrap">
          <table className="tbl">
            <caption className={styles.caption}>
              {t("companies.fileIntake.templateFilesCaption", "Template files")}
            </caption>
            <thead>
              <tr>
                <th>{t("companies.fileIntake.columns.file", "File")}</th>
                <th>{t("companies.fileIntake.columns.keyColumn", "Key column")}</th>
                <th>{t("companies.fileIntake.columns.columns", "Columns")}</th>
                <th>{t("companies.fileIntake.columns.rowsPerDocument", "Rows per document")}</th>
              </tr>
            </thead>
            <tbody>
              {descriptor.data.files.map((file) => (
                <tr key={file.name}>
                  <td>{file.name}</td>
                  <td className="mono">{file.keyColumn}</td>
                  <td>{file.columns.join(", ")}</td>
                  <td>{file.rowsPerDocument}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="card-head">
        <h2 className="grow">{t("companies.fileIntake.batchesHeading", "Batches")}</h2>
      </div>
      {rows.length === 0 ? (
        <div className="card-body">
          <Notice kind="info">{t("companies.fileIntake.emptyBatches", EMPTY_BATCHES)}</Notice>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="tbl">
            <caption className={styles.caption}>
              {t("companies.fileIntake.last50BatchesCaption", "Last 50 batches")}
            </caption>
            <thead>
              <tr>
                <th>{t("companies.fileIntake.columns.filename", "Filename")}</th>
                <th>{t("companies.fileIntake.columns.received", "Received")}</th>
                <th>{t("companies.fileIntake.columns.rows", "Rows")}</th>
                <th>{t("companies.fileIntake.columns.state", "State")}</th>
                <th>{t("companies.fileIntake.columns.detail", "Detail")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((batch) => (
                <BatchRow key={batch.id} batch={batch} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
